// src/pages/TTSLog.js
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { useHistory } from "react-router-dom";
import ReactPaginate from "react-paginate";
import _ from "lodash";
import "./Custom.css";
import "../loader.css";

function TTSLog(props) {
    const [logs, setLogs] = useState([]); // full list
    const [paginatedPosts, setPaginatedPosts] = useState([]); // current page items
    const [recordCount, setRecordCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [filterText, setFilterText] = useState("");
    const [isFocusOnFilterText, setIsFocusOnFilterText] = useState(false);

    const navigate = useHistory();
    const pageSize = 10;

    // Endpoints (replace with your real endpoints)
    const GET_ALL_ENDPOINT = `${process.env.REACT_APP_API_URL}/GetTTSErrorLogs`;
    const SEARCH_ENDPOINT = `${process.env.REACT_APP_API_URL}/SearchTTSErrorLogs`;

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLogs = () => {
        setIsLoading(true);
        axios
            .get(GET_ALL_ENDPOINT)
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                setLogs(data);
                setRecordCount(data.length);
                setCurrentPage(1);
                setPaginatedPosts(data.slice(0, pageSize));
            })
            .catch((err) => {
                console.error("Error fetching TTS logs:", err);
                Swal.fire({
                    title: "Failed",
                    text: "Could not load TTS logs.",
                    icon: "error",
                    confirmButtonText: "Ok",
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const filterLogs = () => {
        setIsLoading(true);
        if (!filterText || filterText.trim() === "") {
            loadLogs();
            return;
        }

        const dataValue = {
            searchString: filterText.trim(),
        };

        // keep header consistent with your other pages (you used multipart/form-data earlier)
        axios({
            method: "post",
            url: SEARCH_ENDPOINT,
            data: dataValue,
            headers: { "Content-Type": "multipart/form-data" },
        })
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                setLogs(data);
                setRecordCount(data.length);
                setCurrentPage(1);
                setPaginatedPosts(data.slice(0, pageSize));
            })
            .catch((err) => {
                console.error("Search error:", err);
                Swal.fire({
                    title: "Failed",
                    text: "Search failed.",
                    icon: "error",
                    confirmButtonText: "Ok",
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const onClearClicked = () => {
        setFilterText("");
        loadLogs();
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && isFocusOnFilterText) {
            filterLogs();
        }
    };

    const handleFocus = () => setIsFocusOnFilterText(true);
    const handleBlur = () => setIsFocusOnFilterText(false);

    const pageCount = recordCount ? Math.ceil(recordCount / pageSize) : 0;

    const handlePageClick = (data) => {
        const selected = data.selected; // zero-based
        const newPage = selected + 1;
        setCurrentPage(newPage);
        const startIndex = selected * pageSize;
        const pageItems = logs.slice(startIndex, startIndex + pageSize);
        setPaginatedPosts(pageItems);
    };

    const BackFunc = () => navigate.push("/index");

    // Date parsing + formatting to Asia/Kolkata
    const parseToLocal = (raw) => {
        if (raw === null || raw === undefined || raw === "") return "";

        // If it's already a Date
        if (raw instanceof Date && !isNaN(raw)) {
            return formatDate(raw);
        }

        // Numeric timestamp?
        if (typeof raw === "number" || /^\d+$/.test(String(raw))) {
            const num = Number(raw);
            // seconds -> ms; heuristic: 10 digits = seconds
            const date = String(num).length === 10 ? new Date(num * 1000) : new Date(num);
            if (!isNaN(date)) return formatDate(date);
        }

        // ISO or other parsable string
        const d = new Date(String(raw));
        if (!isNaN(d)) return formatDate(d);

        // Try convert "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
        const t = String(raw).trim().replace(" ", "T");
        const d2 = new Date(t);
        if (!isNaN(d2)) return formatDate(d2);

        // fallback
        return String(raw);
    };

    const formatDate = (dateValue) => {
        try {
            const dateObj = new Date(dateValue); // Parse ISO string or timestamp
            return dateObj.toLocaleString(undefined, { // undefined → uses browser's locale
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            });
        } catch {
            return String(dateValue ?? ""); // fallback
        }
    };

    const truncateWords = (text, limit = 50) => {
        if (!text) return "";
        const words = text.trim().split(/\s+/);
        return words.length > limit
            ? words.slice(0, limit).join(" ") + "..."
            : text;
    };

    return (
        <>
            <div className="page-content-wrapper">
                {isLoading ? (
                    <div className="page-content d-flex justify-content-center align-items-center">
                        <div className="my-wrapper">
                            <div className="spinner center">
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                                <div className="spinner-blade" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="page-content">
                        <div className="page-bar">
                            <div className="page-title-breadcrumb">
                                <div className="pull-left">
                                    <div className="page-title my-0">Text to Speach Error Logs</div>
                                </div>
                            </div>
                            <div className="page-title-breadcrumb">
                                <div className="pull-right">
                                    <button
                                        type="button"
                                        className="btn d-inline-block"
                                        style={{
                                            backgroundColor: "#1e2b38",
                                            color: "#FFF",
                                            border: "1px solid #1e2b38",
                                        }}
                                        onClick={BackFunc}
                                    >
                                        <i className="fa fa-arrow-left" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-head">
                                        <header>
                                            <div className="d-flex">
                                                <div className="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        value={filterText}
                                                        placeholder="Search by Error Message or StackTrace"
                                                        onChange={(e) => setFilterText(e.target.value)}
                                                        onKeyDown={handleKeyDown}
                                                        onFocus={handleFocus}
                                                        onBlur={handleBlur}
                                                        autoFocus={true}
                                                        style={{
                                                            borderTopRightRadius: "0px",
                                                            borderBottomRightRadius: "0px",
                                                        }}
                                                        className="form-control"
                                                    />
                                                </div>

                                                <button
                                                    type="button"
                                                    className="btn py-2 px-3 me-2 shadow-none"
                                                    onClick={filterLogs}
                                                    style={{
                                                        backgroundColor: "#1e2b38",
                                                        color: "#FFF",
                                                        border: "1px solid #1e2b38",
                                                        borderTopLeftRadius: "0px",
                                                        borderBottomLeftRadius: "0px",
                                                    }}
                                                >
                                                    <i className="fa fa-search" />
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn py-2 px-3 shadow-none"
                                                    onClick={onClearClicked}
                                                    style={{
                                                        backgroundColor: "#ffc63e",
                                                        color: "#000",
                                                        border: "1px solid #ffc63e",
                                                        borderTopLeftRadius: "0px",
                                                        borderBottomLeftRadius: "0px",
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </header>
                                    </div>

                                    <div className="card-body ">
                                        <div className="table-responsive">
                                            <table
                                                className="table table-striped custom-table table-hover"
                                                style={{ tableLayout: "fixed" }}
                                            >
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "5%" }}>Sl.</th>
                                                        <th style={{ width: "15%" }}>Service Name</th> {/* NEW COLUMN */}
                                                        <th style={{ width: "40%" }}>Error Message</th>
                                                        <th style={{ width: "40%" }}>StackTrace</th>
                                                        <th style={{ width: "15%" }}>Date Time</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {recordCount > pageSize
                                                        ? paginatedPosts &&
                                                        paginatedPosts.length > 0 &&
                                                        paginatedPosts.map((item, idx) => (
                                                            <tr key={item.id ?? `p-${idx}`}>
                                                                <td className="text-left">
                                                                    {(currentPage - 1) * pageSize + idx + 1}
                                                                </td>
                                                                <td className="text-left">{item.serviceName ?? ""}</td> {/* NEW CELL */}
                                                                <td
                                                                    style={{
                                                                        whiteSpace: "normal",
                                                                        wordBreak: "break-word",
                                                                        overflowWrap: "break-word",
                                                                    }}
                                                                >
                                                                    {item.errorMessage ?? item.message ?? ""}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        whiteSpace: "normal",
                                                                        wordBreak: "break-word",
                                                                        overflowWrap: "break-word",
                                                                        maxHeight: "6em",
                                                                    }}
                                                                    title={item.stackTrace ?? item.stack ?? ""} // full text on hover
                                                                >
                                                                    {truncateWords(item.stackTrace ?? item.stack ?? "", 10)}
                                                                </td>
                                                                <td className="text-left">
                                                                    {parseToLocal(
                                                                        item.timestamp ??
                                                                        item.dateTime ??
                                                                        item.createdAt ??
                                                                        item.time
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                        : logs &&
                                                        logs.length > 0 &&
                                                        logs.map((item, idx) => (
                                                            <tr key={item.id ?? `s-${idx}`}>
                                                                <td className="text-left">{idx + 1}</td>
                                                                <td className="text-left">{item.serviceName ?? ""}</td> {/* NEW CELL */}
                                                                <td
                                                                    style={{
                                                                        whiteSpace: "normal",
                                                                        wordBreak: "break-word",
                                                                        overflowWrap: "break-word",
                                                                    }}
                                                                >
                                                                    {item.errorMessage ?? item.message ?? ""}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        whiteSpace: "normal",
                                                                        wordBreak: "break-word",
                                                                        overflowWrap: "break-word",
                                                                        maxHeight: "6em",
                                                                    }}
                                                                >
                                                                    {item.stackTrace ?? item.stack ?? ""}
                                                                </td>
                                                                <td className="text-left">
                                                                    {parseToLocal(
                                                                        item.timestamp ??
                                                                        item.dateTime ??
                                                                        item.createdAt ??
                                                                        item.time
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {pageCount > 1 ? (
                                            <ReactPaginate
                                                previousLabel={"Prev"}
                                                nextLabel={"Next"}
                                                breakLabel={"..."}
                                                pageCount={pageCount}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={3}
                                                onPageChange={handlePageClick}
                                                containerClassName={"pagination justify-content-center"}
                                                pageClassName={"page-item"}
                                                pageLinkClassName={"page-link"}
                                                activeClassName={"active"}
                                                previousClassName={"page-item"}
                                                nextClassName={"page-item"}
                                                previousLinkClassName={"page-link"}
                                                nextLinkClassName={"page-link"}
                                            />
                                        ) : (
                                            <div />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default TTSLog;
